import { c as _c } from "react/compiler-runtime";
import { FloatingPanel, type FloatingPanelSizeChangeDetails } from '@ark-ui/react/floating-panel';
import { IconButton } from '@repo/design-system/components/ds/button';
import { Icon } from '@repo/design-system/components/ds/icon';
import { Type } from '@repo/design-system/components/ui/type';
import { css, cva, cx } from '@repo/panda/css';
import type { ReactNode } from 'react';
import { useState } from 'react';
export interface FloatingPanelProps {
  isOpen: boolean;
  onOpenChange?: (details: FloatingPanel.OpenChangeDetails) => void;
  title?: ReactNode;
  children?: ReactNode;
  hideCloseButton?: boolean;
  className?: string;
  variant?: FloatingPanelVariants['variant'];
  resizable?: boolean;
  draggable?: boolean;
  collapsible?: boolean;
  position?: {
    x: number;
    y: number;
  };
  defaultWidth?: number;
  defaultHeight?: number;
  isCollapsed?: boolean;
}
const floatingPanelStyles = cva({
  base: {
    borderRadius: '12',
    boxShadow: 'lg',
    borderWidth: '[1px]',
    borderStyle: 'solid',
    position: 'absolute',
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
    minHeight: '[200px]',
    minWidth: '[200px]'
  },
  variants: {
    variant: {
      dark: {
        color: 'white',
        background: 'grey.900',
        borderColor: 'grey.700',
        boxShadow: 'xl'
      },
      light: {
        color: 'text.primary',
        background: 'wemod.elevated.100',
        borderColor: 'grey.100',
        boxShadow: 'lg'
      }
    }
  },
  defaultVariants: {
    variant: 'dark'
  }
});
export type FloatingPanelVariants = NonNullable<Parameters<typeof floatingPanelStyles>[0]>;
export const FloatingPanelComponent = t0 => {
  const $ = _c(77);
  const {
    isOpen,
    onOpenChange,
    title,
    children,
    hideCloseButton: t1,
    variant,
    className,
    resizable: t2,
    draggable: t3,
    collapsible: t4,
    position,
    defaultWidth: t5,
    defaultHeight: t6,
    isCollapsed: t7
  } = t0;
  const hideCloseButton = t1 === undefined ? false : t1;
  const resizable = t2 === undefined ? false : t2;
  const draggable = t3 === undefined ? true : t3;
  const collapsible = t4 === undefined ? false : t4;
  const defaultWidth = t5 === undefined ? 400 : t5;
  const defaultHeight = t6 === undefined ? 500 : t6;
  const isCollapsed = t7 === undefined ? false : t7;
  const [isCollapsedState, setIsCollapsedState] = useState(isCollapsed);
  let t8;
  if ($[0] !== defaultHeight || $[1] !== defaultWidth) {
    t8 = {
      width: defaultWidth,
      height: defaultHeight
    };
    $[0] = defaultHeight;
    $[1] = defaultWidth;
    $[2] = t8;
  } else {
    t8 = $[2];
  }
  const [size, setSize] = useState(t8);
  const t9 = isCollapsedState ? "[0px]" : "[1px]";
  const t10 = variant === "dark" ? "grey.700" : "grey.100";
  const t11 = variant === "dark" ? "grey.800" : "grey.50";
  const t12 = draggable ? "move" : "default";
  let t13;
  if ($[3] !== t10 || $[4] !== t11 || $[5] !== t12 || $[6] !== t9) {
    t13 = css({
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      padding: "12",
      borderBottomWidth: t9,
      borderBottomStyle: "solid",
      borderBottomColor: t10,
      background: t11,
      cursor: t12,
      userSelect: "none"
    });
    $[3] = t10;
    $[4] = t11;
    $[5] = t12;
    $[6] = t9;
    $[7] = t13;
  } else {
    t13 = $[7];
  }
  const headerStyle = t13;
  let T0;
  let T1;
  let T2;
  let t14;
  let t15;
  let t16;
  let t17;
  let t18;
  let t19;
  let t20;
  let t21;
  let t22;
  let t23;
  if ($[8] !== children || $[9] !== className || $[10] !== collapsible || $[11] !== draggable || $[12] !== headerStyle || $[13] !== hideCloseButton || $[14] !== isCollapsedState || $[15] !== isOpen || $[16] !== onOpenChange || $[17] !== position || $[18] !== resizable || $[19] !== size || $[20] !== title || $[21] !== variant) {
    const bodyStyle = css({
      flex: "1",
      padding: "12",
      overflow: "auto"
    });
    let t24;
    if ($[35] !== className || $[36] !== isCollapsedState || $[37] !== variant) {
      let t25;
      if ($[39] !== isCollapsedState) {
        t25 = isCollapsedState && css({
          height: "auto !important",
          minHeight: "auto !important",
          width: "auto !important",
          minWidth: "[200px]"
        });
        $[39] = isCollapsedState;
        $[40] = t25;
      } else {
        t25 = $[40];
      }
      t24 = cx(floatingPanelStyles({
        variant
      }), className, t25);
      $[35] = className;
      $[36] = isCollapsedState;
      $[37] = variant;
      $[38] = t24;
    } else {
      t24 = $[38];
    }
    const contentStyle = t24;
    let t25;
    if ($[41] === Symbol.for("react.memo_cache_sentinel")) {
      t25 = css({
        marginLeft: "auto"
      });
      $[41] = t25;
    } else {
      t25 = $[41];
    }
    const closeButtonStyle = t25;
    const resizeHandleStyle = css({
      position: "absolute",
      bottom: "[0px]",
      right: "[0px]",
      width: "[16px]",
      height: "[16px]",
      cursor: "se-resize",
      borderTopLeftRadius: "4",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      _after: {
        content: "\"\u22EE\u22EE\"",
        fontSize: "md",
        lineHeight: "none",
        color: variant === "dark" ? "grey.400" : "grey.600",
        transform: "rotate(45deg)",
        letterSpacing: "[-1px]"
      },
      _hover: {
        background: variant === "dark" ? "grey.600" : "grey.300",
        _after: {
          color: variant === "dark" ? "grey.300" : "grey.700"
        }
      }
    });
    T2 = FloatingPanel.Root;
    t19 = size;
    if ($[42] === Symbol.for("react.memo_cache_sentinel")) {
      t20 = e => setSize(e.size);
      $[42] = t20;
    } else {
      t20 = $[42];
    }
    t21 = isOpen;
    t22 = onOpenChange;
    if ($[43] !== position) {
      t23 = position && {
        defaultPosition: position
      };
      $[43] = position;
      $[44] = t23;
    } else {
      t23 = $[44];
    }
    T1 = FloatingPanel.Positioner;
    if ($[45] === Symbol.for("react.memo_cache_sentinel")) {
      t18 = css({
        zIndex: "9999"
      });
      $[45] = t18;
    } else {
      t18 = $[45];
    }
    T0 = FloatingPanel.Content;
    t14 = contentStyle;
    if ($[46] !== collapsible || $[47] !== draggable || $[48] !== headerStyle || $[49] !== hideCloseButton || $[50] !== isCollapsedState || $[51] !== title || $[52] !== variant) {
      t15 = (title || !hideCloseButton) && (draggable ? <FloatingPanel.DragTrigger asChild={true}><div className={css({
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          padding: "12",
          borderBottomWidth: isCollapsedState ? "[0px]" : "[1px]",
          borderBottomStyle: "solid",
          borderBottomColor: variant === "dark" ? "grey.700" : "grey.100",
          background: variant === "dark" ? "grey.800" : "grey.50",
          cursor: "move",
          userSelect: "none"
        })}>{title && <FloatingPanel.Title asChild={true}><Type variant="header-s">{title}</Type></FloatingPanel.Title>}<div className={css({
            display: "flex",
            gap: "4"
          })}>{collapsible && <IconButton variant="ghost" onClick={e_0 => {
              e_0.stopPropagation();
              setIsCollapsedState(!isCollapsedState);
            }} color="primary" size="sm" aria-label={isCollapsedState ? "Expand panel" : "Collapse panel"}><Icon icon={isCollapsedState ? "expand_more" : "expand_less"} decorative={true} /></IconButton>}{!hideCloseButton && <FloatingPanel.CloseTrigger asChild={true} className={closeButtonStyle}><IconButton variant="ghost" color="primary" size="sm" aria-label="Close panel"><Icon icon="close" decorative={true} /></IconButton></FloatingPanel.CloseTrigger>}</div></div></FloatingPanel.DragTrigger> : <FloatingPanel.Header className={headerStyle}>{title && <FloatingPanel.Title asChild={true}><Type variant="header-s">{title}</Type></FloatingPanel.Title>}<div className={css({
          display: "flex",
          gap: "4"
        })}>{collapsible && <IconButton variant="ghost" onClick={() => setIsCollapsedState(!isCollapsedState)} color="primary" size="sm" aria-label={isCollapsedState ? "Expand panel" : "Collapse panel"}><Icon icon={isCollapsedState ? "expand_more" : "expand_less"} decorative={true} /></IconButton>}{!hideCloseButton && <FloatingPanel.CloseTrigger asChild={true} className={closeButtonStyle}><IconButton variant="ghost" color="primary" size="sm" aria-label="Close panel"><Icon icon="close" decorative={true} /></IconButton></FloatingPanel.CloseTrigger>}</div></FloatingPanel.Header>);
      $[46] = collapsible;
      $[47] = draggable;
      $[48] = headerStyle;
      $[49] = hideCloseButton;
      $[50] = isCollapsedState;
      $[51] = title;
      $[52] = variant;
      $[53] = t15;
    } else {
      t15 = $[53];
    }
    let t26;
    if ($[54] !== isCollapsedState) {
      t26 = isCollapsedState && css({
        display: "none"
      });
      $[54] = isCollapsedState;
      $[55] = t26;
    } else {
      t26 = $[55];
    }
    const t27 = cx(bodyStyle, t26);
    if ($[56] !== children || $[57] !== t27) {
      t16 = <FloatingPanel.Body className={t27}>{children}</FloatingPanel.Body>;
      $[56] = children;
      $[57] = t27;
      $[58] = t16;
    } else {
      t16 = $[58];
    }
    t17 = resizable && <FloatingPanel.ResizeTrigger axis="se" className={cx(resizeHandleStyle, isCollapsedState && css({
      display: "none"
    }))} />;
    $[8] = children;
    $[9] = className;
    $[10] = collapsible;
    $[11] = draggable;
    $[12] = headerStyle;
    $[13] = hideCloseButton;
    $[14] = isCollapsedState;
    $[15] = isOpen;
    $[16] = onOpenChange;
    $[17] = position;
    $[18] = resizable;
    $[19] = size;
    $[20] = title;
    $[21] = variant;
    $[22] = T0;
    $[23] = T1;
    $[24] = T2;
    $[25] = t14;
    $[26] = t15;
    $[27] = t16;
    $[28] = t17;
    $[29] = t18;
    $[30] = t19;
    $[31] = t20;
    $[32] = t21;
    $[33] = t22;
    $[34] = t23;
  } else {
    T0 = $[22];
    T1 = $[23];
    T2 = $[24];
    t14 = $[25];
    t15 = $[26];
    t16 = $[27];
    t17 = $[28];
    t18 = $[29];
    t19 = $[30];
    t20 = $[31];
    t21 = $[32];
    t22 = $[33];
    t23 = $[34];
  }
  let t24;
  if ($[59] !== T0 || $[60] !== t14 || $[61] !== t15 || $[62] !== t16 || $[63] !== t17) {
    t24 = <T0 className={t14}>{t15}{t16}{t17}</T0>;
    $[59] = T0;
    $[60] = t14;
    $[61] = t15;
    $[62] = t16;
    $[63] = t17;
    $[64] = t24;
  } else {
    t24 = $[64];
  }
  let t25;
  if ($[65] !== T1 || $[66] !== t18 || $[67] !== t24) {
    t25 = <T1 className={t18}>{t24}</T1>;
    $[65] = T1;
    $[66] = t18;
    $[67] = t24;
    $[68] = t25;
  } else {
    t25 = $[68];
  }
  let t26;
  if ($[69] !== T2 || $[70] !== t19 || $[71] !== t20 || $[72] !== t21 || $[73] !== t22 || $[74] !== t23 || $[75] !== t25) {
    t26 = <T2 size={t19} onSizeChange={t20} open={t21} onOpenChange={t22} {...t23}>{t25}</T2>;
    $[69] = T2;
    $[70] = t19;
    $[71] = t20;
    $[72] = t21;
    $[73] = t22;
    $[74] = t23;
    $[75] = t25;
    $[76] = t26;
  } else {
    t26 = $[76];
  }
  return t26;
};
export { FloatingPanelComponent as FloatingPanel };